import React, { useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import './styles/main.scss';
import api from './services/api';
import { AuthProvider, useAuth } from './context/AuthContext';
import { DepartmentProvider, useDepartment } from './context/DepartmentContext';
import Nav from './components/Nav/Nav';
import RequireAuth from './components/RequireAuth';
import TableView from './views/TableView';
import Insights from './views/Insights';
import MentoringPage from './views/MentoringPage';
import AdminPage from './views/AdminPage';
import LoginPage from './views/LoginPage';

function AppRoutes() {
  const { user } = useAuth();
  const { selectedDepartmentId } = useDepartment();
  const location = useLocation();
  const isLoginPage = location.pathname === '/login';
  const isAdminPage = location.pathname === '/admin' || location.pathname.startsWith('/admin/');
  
  // Shared state
  const [skills, setSkills] = useState([]);
  const [skillGroups, setSkillGroups] = useState([]);
  const [levels, setLevels] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Filtered state for non-admin pages
  const [filteredSkills, setFilteredSkills] = useState([]);
  const [filteredSkillGroups, setFilteredSkillGroups] = useState([]);
  const [filteredLevels, setFilteredLevels] = useState([]);
  const [filteredTeamMembers, setFilteredTeamMembers] = useState([]);

  // Fetch functions
  const loadSkills = async () => {
    try {
      const response = await api.getSkills();
      setSkills(response.data);
    } catch (error) {
      console.error('Error loading skills:', error);
    }
  };
  const loadSkillGroups = async () => {
    try {
      const response = await api.getSkillGroups();
      setSkillGroups(response.data);
    } catch (error) {
      console.error('Error loading skill groups:', error);
    }
  };
  const loadLevels = async () => {
    try {
      const response = await api.getLevels();
      setLevels(response.data);
    } catch (error) {
      console.error('Error loading levels:', error);
    }
  };
  const loadTeamMembers = async () => {
    try {
      const response = await api.getTeamMembers();
      setTeamMembers(response.data);
    } catch (error) {
      console.error('Error loading team members:', error);
    }
  };
  const loadDepartments = async () => {
    try {
      const response = await api.getDepartments();
      setDepartments(response.data);
    } catch (error) {
      console.error('Error loading departments:', error);
    }
  };

  // Centralized reload function (synchronous, departments first, then skillGroups)
  const reloadAll = useCallback(async () => {
    await loadDepartments();
    await loadSkillGroups();
    await loadSkills();
    await loadLevels();
    await loadTeamMembers();
  }, []);

  // Initial load
  useEffect(() => {
    reloadAll();
  }, [reloadAll]);

  // Filter data based on selected department for non-admin pages
  useEffect(() => {
    if (!isAdminPage && selectedDepartmentId) {
      setFilteredSkills(skills.filter(skill => skill.department_id === parseInt(selectedDepartmentId)));
      setFilteredSkillGroups(skillGroups.filter(group => group.department_id === parseInt(selectedDepartmentId)));
      setFilteredLevels(levels.filter(level => level.department_id === parseInt(selectedDepartmentId)));
      setFilteredTeamMembers(teamMembers.filter(member => member.department_id === parseInt(selectedDepartmentId)));
    } else {
      setFilteredSkills(skills);
      setFilteredSkillGroups(skillGroups);
      setFilteredLevels(levels);
      setFilteredTeamMembers(teamMembers);
    }
  }, [selectedDepartmentId, skills, skillGroups, levels, teamMembers, isAdminPage]);

  // Callback to refresh team member data when changes occur
  const onDataChange = useCallback(async () => {
    await loadTeamMembers();
  }, []);

  return (
    <div className="container">
      {!isLoginPage && <Nav />}
      <main>
        <Routes>
          <Route
            path="/login"
            element={<LoginPage />}
          />
          <Route
            path="/"
            element={
              <RequireAuth>
                <TableView
                  key={JSON.stringify(filteredSkillGroups.map(g => g.id))}
                  skills={filteredSkills}
                  skillGroups={filteredSkillGroups}
                  levels={filteredLevels}
                  teamMembers={filteredTeamMembers}
                  onDataChange={onDataChange}
                />
              </RequireAuth>
            }
          />
          <Route
            path="/insights"
            element={
              <RequireAuth>
                <Insights
                  skills={filteredSkills}
                  skillGroups={filteredSkillGroups}
                  levels={filteredLevels}
                  teamMembers={filteredTeamMembers}
                />
              </RequireAuth>
            }
          />
          <Route
            path="/mentoring"
            element={
              <RequireAuth>
                <MentoringPage
                  skills={filteredSkills}
                  skillGroups={filteredSkillGroups}
                  levels={filteredLevels}
                  teamMembers={filteredTeamMembers}
                  currentUser={user}
                />
              </RequireAuth>
            }
          />
          <Route
            path="/admin"
            element={
              <RequireAuth adminOnly>
                <AdminPage
                  skills={skills}
                  skillGroups={skillGroups}
                  levels={levels}
                  departments={departments}
                  reloadAll={reloadAll}
                />
              </RequireAuth>
            }
          />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <DepartmentProvider>
          <AppRoutes />
        </DepartmentProvider>
      </Router>
    </AuthProvider>
  );
}

export default App; 